'use client';

import { useEffect } from 'react';
import {
  ExperimentAssignment,
  readExperimentAssignments,
  registerRenderedExperimentAssignments,
} from '@/utils/experiment-helper';
import { confirmExperimentAssignments } from './experiment-assignment-effect-actions';

// Module-level, so it survives remounts and soft navigations within the same document: React strict
// mode double-mounts effects and a route change re-renders the wrapper, neither of which is a new
// exposure. Keyed per assignment rather than a single flag because a session can legitimately pick up
// a second, page-scoped experiment after the first one was confirmed.
const confirmedAssignmentKeys = new Set<string>();

const assignmentKey = ({ name, variant }: ExperimentAssignment) => `${name}:${variant}`;

interface ExperimentAssignmentEffectProps {
  assignments: ExperimentAssignment[];
}

/**
 * Confirms the rendered arms once per session: writes the `var` cookie and posts the exposures in
 * one batched round trip, so several wrappers on one page cannot drop each other's confirmation.
 *
 * Renders nothing. It exists as a component rather than a hook so the renderer can mount it purely
 * as a consequence of having actually rendered experiment arms.
 */
export function ExperimentAssignmentEffect({ assignments }: ExperimentAssignmentEffectProps) {
  // Registered during render, NOT in the effect: sibling components fire view_item & co from their
  // own mount effects, and render always precedes every effect — this is what gives the very first
  // tracking events of the document their experiment attribution, before the cookie round trip has
  // landed. Idempotent, and browser-guarded inside, so the SSR pass never touches module state.
  registerRenderedExperimentAssignments(assignments);

  useEffect(() => {
    // The cookie is the cross-page-load guard: an assignment already recorded on an earlier page of
    // this session needs no confirmation and no request.
    const recorded = readExperimentAssignments();

    const toConfirm = assignments.filter((assignment) => {
      const key = assignmentKey(assignment);

      if (confirmedAssignmentKeys.has(key)) {
        return false;
      }

      confirmedAssignmentKeys.add(key);

      return !recorded.some(
        (entry) => entry.name === assignment.name && entry.variant === assignment.variant,
      );
    });

    if (toConfirm.length === 0) {
      return;
    }

    confirmExperimentAssignments(toConfirm).catch(() => {
      // Fire-and-forget: the action already logs its own failures server-side, and a failed round
      // trip must not surface in the browser as an unhandled rejection.
      //
      // The keys are claimed up front (above) so a double-mount or a concurrent render cannot fire
      // the same confirmation twice, and released again here: without this, a single failed round
      // trip would suppress the retry for the rest of the document, and a visitor soft-navigating
      // through an SPA session would keep seeing the arm while never being recorded as exposed to
      // it. The `var` cookie is still unset, so the next render of this assignment retries.
      toConfirm.forEach((assignment) => {
        confirmedAssignmentKeys.delete(assignmentKey(assignment));
      });
    });
    // Re-runs on identity changes are harmless: the key set above makes them no-ops.
  }, [assignments]);

  return null;
}
